import { CONNECTOR_MAPPABLE_TYPES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon } from "@sagot/ui";
import type { HttpRequestStep } from "../types";
import type { ConnectorTestOut } from "./connector-api-types";
import { errorKeyOf } from "./connector-model";

const C = "engage.workflows.connector.test";

interface ResultProps {
  result: ConnectorTestOut;
  step: HttpRequestStep;
  disabled: boolean;
  onUse: (pointer: string) => void;
}

function Verdict({ result }: { result: ConnectorTestOut }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1" data-testid="connector-verdict">
      <p className={result.ok ? "flex items-center gap-2 text-sm font-semibold text-success" : "flex items-center gap-2 text-sm font-semibold text-danger"}>
        <Icon name={result.ok ? "check-circle" : "alert-triangle"} className="size-4 shrink-0" />
        {t(`${C}.${result.ok ? "succeeded" : "failed"}`)}
      </p>
      <p className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
        <Badge tone={result.ok ? "success" : "danger"}>{result.status > 0 ? t(`${C}.status`, { status: result.status }) : t(`${C}.noStatus`)}</Badge>
        <span className="font-mono">{t(`${C}.duration`, { ms: result.duration_ms })}</span>
      </p>
      {result.ok ? null : <p className="text-xs text-fg-muted">{t(`engage.workflows.connector.errors.${errorKeyOf(result.error_type)}`)}</p>}
    </div>
  );
}

function Mapped({ result }: { result: ConnectorTestOut }) {
  const { t } = useTranslation();
  const entries = Object.entries(result.mapped);
  return (
    <div>
      <h4 className="label">{t(`${C}.mappedTitle`)}</h4>
      {entries.length === 0 ? (
        <p className="text-xs text-fg-muted">{t(`${C}.mappedNone`)}</p>
      ) : (
        <dl className="space-y-1 text-xs" data-testid="connector-mapped">
          {entries.map(([name, value]) => (
            <div key={name} className="flex flex-wrap gap-x-2">
              <dt className="font-mono font-semibold">{name}</dt>
              <dd className="break-all font-mono text-fg-muted">{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {result.skipped.length > 0 ? (
        <div className="mt-2">
          <h4 className="label">{t(`${C}.skippedTitle`)}</h4>
          <ul className="space-y-0.5 text-xs text-warning">
            {result.skipped.map((skip) => <li key={skip.to}>{t(`${C}.skipped.${skip.reason}`, { to: skip.to })}</li>)}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** What a test request found out: how it went, what would be saved, and the fields of the answer to pick from (never their contents). */
export function ConnectorTestResult({ result, step, disabled, onUse }: ResultProps) {
  const { t } = useTranslation();
  const mappedPointers = new Set((step.map ?? []).map((row) => row.from));
  return (
    <div className="space-y-4 rounded-md border border-line p-3" data-testid="connector-result">
      <h3 className="sr-only">{t(`${C}.resultTitle`)}</h3>
      <Verdict result={result} />
      <Mapped result={result} />
      {result.pointers.length > 0 ? (
        <div>
          <h4 className="label">{t(`${C}.fieldsTitle`)}</h4>
          <ul className="divide-y divide-line text-xs" data-testid="connector-pointers">
            {result.pointers.map((field) => {
              const mappable = CONNECTOR_MAPPABLE_TYPES.has(field.type);
              return (
                <li key={field.pointer} className="flex flex-wrap items-center gap-2 py-1.5">
                  <code className="min-w-0 flex-1 break-all font-mono">{field.pointer}</code>
                  <Badge>{field.type}</Badge>
                  {mappedPointers.has(field.pointer) ? (
                    <span className="flex items-center gap-1 font-semibold text-success"><Icon name="check" className="size-3.5" />{t(`${C}.mapped`)}</span>
                  ) : (
                    <Button type="button" variant="secondary" size="sm" disabled={disabled || !mappable} title={mappable ? undefined : t(`${C}.notMappable`)} aria-label={t(`${C}.useFieldFor`, { pointer: field.pointer })} onClick={() => { onUse(field.pointer); }}>
                      {t(`${C}.useField`)}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : result.ok ? (
        <p className="text-xs text-fg-muted">{t(`${C}.fieldsNone`)}</p>
      ) : null}
    </div>
  );
}
